import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, KeyRound, PhoneCall, ShieldCheck, Video } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation } from 'react-router';
import { z } from 'zod';
import { loginSchema, type MfaChallenge } from '@brimostech/shared';
import { Button, Input, Label, Spinner } from '@brimostech/ui';
import { Brand } from '@/components/brand';
import { ApiError } from '@/lib/api-client';
import { useAuth } from './auth-context';

const formSchema = loginSchema.pick({ identifier: true, password: true });
type FormValues = z.infer<typeof formSchema>;

const mfaSchema = z.object({
  code: z.string().trim().max(24).optional(),
});

const HIGHLIGHTS = [
  { icon: ShieldCheck, text: 'Reconhecimento facial nos terminais Control iD' },
  { icon: Video, text: 'Câmeras Hikvision ao vivo na web e no app' },
  { icon: PhoneCall, text: 'Interfone que toca no celular do morador' },
];

function deviceName(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Navegador';
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /Mac OS/.test(ua)
      ? 'macOS'
      : /Android/.test(ua)
        ? 'Android'
        : /iPhone|iPad/.test(ua)
          ? 'iOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';
  return os ? `${browser} no ${os}` : browser;
}

export function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-[linear-gradient(160deg,var(--brand)_0%,var(--brand-deep)_100%)] p-10 text-brand-foreground lg:flex">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 -right-32 size-96 rounded-full bg-white/10 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-40 -left-24 size-96 rounded-full bg-black/20 blur-3xl"
        />
        <Brand inverted />
        <div className="relative max-w-md space-y-8">
          <h2 className="text-3xl leading-tight font-semibold text-balance">
            Controle de acesso, câmeras e portaria digital em um só lugar.
          </h2>
          <ul className="space-y-4">
            {HIGHLIGHTS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-sm text-brand-foreground/90">
                <span className="flex size-9 items-center justify-center rounded-lg bg-white/10">
                  <Icon className="size-4" />
                </span>
                {text}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-brand-foreground/70">
          Feito para administradoras, síndicos, portarias e moradores.
        </p>
      </aside>
      <main className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm space-y-8">
          <div className="lg:hidden">
            <Brand />
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}

export function LoginPage() {
  const { status, login, verifyMfa } = useAuth();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  const [useRecovery, setUseRecovery] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { identifier: '', password: '' },
  });
  const mfaForm = useForm<z.infer<typeof mfaSchema>>({
    resolver: zodResolver(mfaSchema),
    defaultValues: { code: '' },
  });

  const from = (location.state as { from?: string } | null)?.from ?? '/';
  if (status === 'authenticated') return <Navigate to={from} replace />;

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      const result = await login({ ...values, deviceName: deviceName() });
      if (result.mfa) setChallenge(result.mfa);
    } catch (error) {
      setServerError(
        error instanceof ApiError ? error.message : 'Não foi possível entrar. Tente novamente.',
      );
    }
  });

  const onSubmitMfa = mfaForm.handleSubmit(async (values) => {
    if (!challenge) return;
    setServerError(null);
    const code = (values.code ?? '').trim();
    if (!code) {
      mfaForm.setError('code', {
        message: useRecovery ? 'Informe um código de recuperação' : 'Informe o código de 6 dígitos',
      });
      return;
    }
    try {
      await verifyMfa(
        useRecovery
          ? { mfaToken: challenge.mfaToken, recoveryCode: code }
          : { mfaToken: challenge.mfaToken, code },
      );
    } catch (error) {
      setServerError(
        error instanceof ApiError ? error.message : 'Não foi possível verificar o código.',
      );
      if (error instanceof ApiError && error.status === 401 && /expirou/.test(error.message)) {
        setChallenge(null);
      }
    }
  });

  const { errors, isSubmitting } = form.formState;

  if (challenge) {
    return (
      <AuthLayout>
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Verificação em duas etapas</h1>
          <p className="text-sm text-muted-foreground">
            {useRecovery
              ? 'Informe um dos seus códigos de recuperação.'
              : 'Digite o código de 6 dígitos do seu aplicativo autenticador.'}
          </p>
        </div>
        <form onSubmit={onSubmitMfa} className="space-y-5" noValidate>
          <div className="space-y-2">
            <Label htmlFor="mfa-code">{useRecovery ? 'Código de recuperação' : 'Código'}</Label>
            <Input
              id="mfa-code"
              autoFocus
              autoComplete="one-time-code"
              inputMode={useRecovery ? 'text' : 'numeric'}
              placeholder={useRecovery ? 'XXXXX-XXXXX' : '000000'}
              className={useRecovery ? '' : 'text-center text-lg tracking-[0.4em]'}
              aria-invalid={!!mfaForm.formState.errors.code}
              {...mfaForm.register('code')}
            />
            {mfaForm.formState.errors.code ? (
              <p className="text-sm text-destructive">{mfaForm.formState.errors.code.message}</p>
            ) : null}
          </div>
          {serverError ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {serverError}
            </div>
          ) : null}
          <Button type="submit" className="w-full" disabled={mfaForm.formState.isSubmitting}>
            {mfaForm.formState.isSubmitting ? <Spinner /> : <KeyRound />}
            Verificar
          </Button>
          <div className="flex justify-between text-sm">
            <button
              type="button"
              className="text-muted-foreground underline-offset-4 hover:underline"
              onClick={() => setUseRecovery((v) => !v)}
            >
              {useRecovery ? 'Usar o aplicativo autenticador' : 'Usar um código de recuperação'}
            </button>
            <button
              type="button"
              className="text-muted-foreground underline-offset-4 hover:underline"
              onClick={() => setChallenge(null)}
            >
              Voltar
            </button>
          </div>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Entrar</h1>
        <p className="text-sm text-muted-foreground">Use seu e-mail ou celular e a sua senha.</p>
      </div>

      <form onSubmit={onSubmit} className="space-y-5" noValidate>
        <div className="space-y-2">
          <Label htmlFor="identifier">E-mail ou celular</Label>
          <Input
            id="identifier"
            type="text"
            autoComplete="username"
            inputMode="email"
            placeholder="voce@exemplo.com.br"
            aria-invalid={!!errors.identifier}
            aria-describedby={errors.identifier ? 'identifier-error' : undefined}
            {...form.register('identifier')}
          />
          {errors.identifier ? (
            <p id="identifier-error" className="text-sm text-destructive">
              {errors.identifier.message}
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Senha</Label>
            <Link
              to="/esqueci-senha"
              className="text-xs text-muted-foreground underline-offset-4 hover:underline"
            >
              Esqueci minha senha
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Sua senha"
              className="pr-10"
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? 'password-error' : undefined}
              {...form.register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
          {errors.password ? (
            <p id="password-error" className="text-sm text-destructive">
              {errors.password.message}
            </p>
          ) : null}
        </div>

        {serverError ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {serverError}
          </div>
        ) : null}

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner /> Entrando...
            </>
          ) : (
            'Entrar'
          )}
        </Button>
      </form>

      <p className="text-xs text-muted-foreground">
        Recebeu um convite?{' '}
        <Link to="/convite" className="underline-offset-4 hover:underline">
          Ative sua conta aqui
        </Link>
        . Problemas para entrar? Fale com a administração do seu condomínio.
      </p>
    </AuthLayout>
  );
}
